import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button, cn } from '@mlm/ui';
import type { Membership } from '../../../lib/api';

/**
 * Per-tier `mentor_bonus` editor. Each receiver tier stores
 *   config.computation.mentor_bonus = {
 *     amount: { [level]: percent },        // % of buyer order PV per upline level
 *     requirement: { [level]: minDirects },// directs needed to earn at that level
 *     limit, limit_count,                  // per-period caps
 *   }
 * Level tabs select a level; the table shows per-tier % + min-directs for it.
 * A separate caps table holds the two per-tier per-period limits.
 */
type Block = Record<string, unknown>;

export function MentorBonusPerTier({
  memberships,
  draft,
  onChange,
  readOnly,
}: {
  memberships: Membership[];
  draft: Record<string, Record<string, unknown>>;
  onChange: (id: string, next: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  function block(cfg: Record<string, unknown>): Block {
    const computation = (cfg.computation as Block | undefined) ?? {};
    return (computation.mentor_bonus as Block | undefined) ?? {};
  }
  function writeBlock(id: string, nextBlock: Block) {
    const current = draft[id] ?? {};
    const computation = { ...((current.computation as Block | undefined) ?? {}) };
    const cleaned: Block = {};
    for (const [k, v] of Object.entries(nextBlock)) {
      if (v === undefined) continue;
      if (typeof v === 'object' && v !== null && Object.keys(v as object).length === 0) continue;
      cleaned[k] = v;
    }
    if (Object.keys(cleaned).length === 0) delete computation.mentor_bonus;
    else computation.mentor_bonus = cleaned;
    const next = { ...current };
    if (Object.keys(computation).length === 0) delete next.computation;
    else next.computation = computation;
    onChange(id, next);
  }

  const presentLevels = useMemo(() => {
    const set = new Set<number>();
    for (const cfg of Object.values(draft)) {
      const amount = block(cfg).amount as Block | undefined;
      if (amount) for (const k of Object.keys(amount)) {
        const n = Number(k);
        if (Number.isFinite(n) && n >= 1) set.add(n);
      }
    }
    if (set.size === 0) set.add(1);
    return Array.from(set).sort((a, b) => a - b);
  }, [draft]);

  const [extra, setExtra] = useState<number[]>([]);
  const levels = useMemo(() => {
    const set = new Set<number>(presentLevels);
    for (const n of extra) set.add(n);
    return Array.from(set).sort((a, b) => a - b);
  }, [presentLevels, extra]);
  const [active, setActive] = useState<number>(levels[0] ?? 1);

  function getMap(cfg: Record<string, unknown>, mapKey: 'amount' | 'requirement', level: number): number {
    const map = (block(cfg)[mapKey] as Block | undefined) ?? {};
    const v = map[String(level)];
    return typeof v === 'number' || typeof v === 'string' ? Number(v) || 0 : 0;
  }
  function setMap(id: string, mapKey: 'amount' | 'requirement', level: number, n: number) {
    const b = { ...block(draft[id] ?? {}) };
    const map = { ...((b[mapKey] as Block | undefined) ?? {}) };
    if (n === 0) delete map[String(level)];
    else map[String(level)] = n;
    b[mapKey] = map;
    writeBlock(id, b);
  }
  function getCap(cfg: Record<string, unknown>, key: 'limit' | 'limit_count'): number {
    const v = block(cfg)[key];
    return typeof v === 'number' || typeof v === 'string' ? Number(v) || 0 : 0;
  }
  function setCap(id: string, key: 'limit' | 'limit_count', n: number) {
    const b = { ...block(draft[id] ?? {}) };
    if (n === 0) delete b[key];
    else b[key] = n;
    writeBlock(id, b);
  }

  function addLevel() {
    const next = Math.max(...levels, 0) + 1;
    setExtra((prev) => [...prev, next]);
    setActive(next);
  }
  function removeLevel(level: number) {
    for (const m of memberships) {
      setMap(m.id, 'amount', level, 0);
      setMap(m.id, 'requirement', level, 0);
    }
    setExtra((prev) => prev.filter((l) => l !== level));
    if (active === level) setActive(levels.filter((l) => l !== level)[0] ?? 1);
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-[10px] text-muted-foreground">
          % of the buyer's order PV paid to each upline level, with an optional
          min-directs requirement to earn at that level. Rows = receiver tier.
        </p>
        <div className="flex items-center gap-1 flex-wrap">
          {levels.map((lvl) => (
            <button
              key={lvl}
              type="button"
              onClick={() => setActive(lvl)}
              className={cn(
                'h-8 px-3 rounded-md text-xs font-mono border transition-colors',
                active === lvl ? 'bg-primary/10 border-primary text-primary' : 'border-input text-muted-foreground hover:bg-muted',
              )}
            >
              Level {lvl}
            </button>
          ))}
          {!readOnly ? (
            <>
              <Button size="sm" variant="outline" onClick={addLevel}>
                <Plus className="h-3 w-3 mr-1" />
                Level
              </Button>
              {levels.length > 1 ? (
                <Button size="sm" variant="ghost" onClick={() => removeLevel(active)} className="text-destructive">
                  <Trash2 className="h-3 w-3 mr-1" />
                  Remove L{active}
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-left text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Tier</th>
                <th className="px-3 py-2 font-medium">Percent %</th>
                <th className="px-3 py-2 font-medium">Min directs</th>
              </tr>
            </thead>
            <tbody>
              {memberships.map((m) => (
                <tr key={m.id} className="border-t">
                  <td className="px-3 py-1.5 text-xs font-mono">{m.code}</td>
                  <td className="px-2 py-1.5">
                    <input
                      type="number"
                      className="h-8 w-24 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                      value={getMap(draft[m.id] ?? {}, 'amount', active)}
                      disabled={readOnly}
                      step={0.01}
                      min={0}
                      onChange={(e) => setMap(m.id, 'amount', active, Number(e.target.value) || 0)}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      type="number"
                      className="h-8 w-24 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                      value={getMap(draft[m.id] ?? {}, 'requirement', active)}
                      disabled={readOnly}
                      step={1}
                      min={0}
                      onChange={(e) => setMap(m.id, 'requirement', active, Number(e.target.value) || 0)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-[10px] text-muted-foreground">
          Per-period caps (0 = no cap). <strong>Total cap</strong> bounds the
          summed mentor-bonus amount; <strong>Row cap</strong> bounds the number
          of mentor-bonus payouts.
        </p>
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-left text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Tier</th>
                <th className="px-3 py-2 font-medium">Total amount cap</th>
                <th className="px-3 py-2 font-medium">Row count cap</th>
              </tr>
            </thead>
            <tbody>
              {memberships.map((m) => (
                <tr key={m.id} className="border-t">
                  <td className="px-3 py-1.5 text-xs font-mono">{m.code}</td>
                  <td className="px-2 py-1.5">
                    <input
                      type="number"
                      className="h-8 w-28 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                      value={getCap(draft[m.id] ?? {}, 'limit')}
                      disabled={readOnly}
                      step={0.01}
                      min={0}
                      onChange={(e) => setCap(m.id, 'limit', Number(e.target.value) || 0)}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      type="number"
                      className="h-8 w-24 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                      value={getCap(draft[m.id] ?? {}, 'limit_count')}
                      disabled={readOnly}
                      step={1}
                      min={0}
                      onChange={(e) => setCap(m.id, 'limit_count', Number(e.target.value) || 0)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
